import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'

import { TopBar } from '@/components/layouts/TopBar'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { LineSupplyDialog } from '@/components/features/supply/LineSupplyDialog'
import { SupplyModeChip } from '@/components/features/supply/SupplyModeChip'
import { originQuantityHint, summarizeSupply } from '@/components/features/supply/supply-labels'
import {
  getSaleSupply, getSupplyOptions, setSaleSupply,
  type SaleSupply, type SaleSupplyLine, type SetLinesSupplyBody, type SupplyOption,
} from '@/components/features/supply/supply.api'
import { ApiError } from '@/lib/api/client'
import { formatCurrency } from '@/lib/format'
import { showErrorToast, showSuccessToast } from '@/lib/toast'
import { cn } from '@/lib/utils'

type Filter = 'all' | 'todo'

interface CategoryGroup { category: string; lines: SaleSupplyLine[] }

/** Groups by category in the sale's order; the first appearance fixes the group's place. */
function groupByCategory(lines: SaleSupplyLine[]): CategoryGroup[] {
  const groups = new Map<string, SaleSupplyLine[]>()
  for (const line of lines) {
    const category = line.category?.trim() || 'Autres'
    if (!groups.has(category)) groups.set(category, [])
    groups.get(category)!.push(line)
  }
  return [...groups].map(([category, grouped]) => ({ category, lines: grouped }))
}

const isToDo = (l: SaleSupplyLine) => l.supply.mode === 'unqualified' || (l.supply.mode === 'buy' && !l.supply.supplierId)

/**
 * SOLY-IMPORT-003 S11 — « Vente, colonne Appro »: how each sold line is supplied, one line or many
 * at once. A line a supplier already holds is frozen here (the server says which).
 */
export default function ProjectSaleSupplyPage() {
  const { id: projectId } = useParams<{ id: string }>()
  const [sale, setSale] = useState<SaleSupply | null>(null)
  const [suppliers, setSuppliers] = useState<SupplyOption[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [filter, setFilter] = useState<Filter>('all')
  const [editing, setEditing] = useState<SaleSupplyLine | null>(null)
  const [bulkOpen, setBulkOpen] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!projectId) return
    let active = true
    void (async () => {
      try {
        const [loaded, options] = await Promise.all([getSaleSupply(projectId), getSupplyOptions(projectId)])
        if (!active) return
        setSale(loaded)
        setSuppliers(options)
        setError(null)
      } catch (e) {
        if (!active) return
        setError(e instanceof ApiError && e.code === 'ORDER_NOT_FOUND'
          ? 'Aucune vente sur ce projet : acceptez un devis d’abord.'
          : 'La vente n’a pas pu être chargée.')
      } finally {
        if (active) setLoading(false)
      }
    })()
    return () => { active = false }
  }, [projectId])

  const supplierNames = useMemo(() => new Map(suppliers.map((s) => [s.id, s.name])), [suppliers])
  const summary = useMemo(() => summarizeSupply(sale?.lines ?? []), [sale])
  const visible = useMemo(
    () => (sale?.lines ?? []).filter((l) => filter === 'all' || isToDo(l)),
    [sale, filter],
  )
  const groups = useMemo(() => groupByCategory(visible), [visible])
  const canWrite = !!sale?.canWrite

  const save = useCallback(async (lineIds: string[], body: Omit<SetLinesSupplyBody, 'lineIds'>) => {
    if (!projectId) return
    const updated = await setSaleSupply(projectId, { ...body, lineIds })
    setSale(updated)
    setSelected(new Set())
  }, [projectId])

  const applyToSelection = async (mode: 'make' | 'none') => {
    setBusy(true)
    try {
      await save([...selected], { mode })
      showSuccessToast(`${selected.size} ligne${selected.size > 1 ? 's' : ''} : ${mode === 'make' ? 'Fabriquer' : 'Aucun'}`)
    } catch (e) {
      showErrorToast(e instanceof Error && e.message ? e.message : 'L’appro n’a pas été enregistré.')
    } finally {
      setBusy(false)
    }
  }

  const toggle = (lineId: string, on: boolean) => setSelected((prev) => {
    const next = new Set(prev)
    if (on) next.add(lineId)
    else next.delete(lineId)
    return next
  })

  return (
    <div className="min-h-screen bg-muted/30">
      <TopBar />
      <div className="mx-auto max-w-6xl space-y-4 px-4 py-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Button asChild variant="ghost" size="icon" className="h-8 w-8">
            <Link to={`/projects/${projectId}`} aria-label="Retour au projet" data-testid="sale-supply-back">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <span>Projet › Vente</span>
        </div>

        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-80" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : error || !sale ? (
          <div className="rounded-lg border bg-background p-4 text-sm text-muted-foreground" data-testid="sale-supply-error">{error}</div>
        ) : (
          <>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div className="space-y-1">
                <h1 className="text-xl font-semibold">
                  Vente{sale.totalWithTax != null ? ` · ${formatCurrency(sale.totalWithTax)} TTC` : ''}
                </h1>
                <p className="text-sm text-muted-foreground" data-testid="sale-supply-summary">
                  {summary.lineCount} lignes · <b className="text-foreground">{summary.toBuy} à acheter</b>
                  {summary.supplierCount > 0 ? ` chez ${summary.supplierCount} fournisseur${summary.supplierCount > 1 ? 's' : ''}` : ''}
                  {summary.missingSupplier > 0 ? <span className="text-amber-800"> · {summary.missingSupplier} fournisseur à préciser</span> : null}
                  {summary.unqualified > 0 ? <span className="text-amber-800"> · {summary.unqualified} à préciser</span> : null}
                  {` · ${summary.noPurchase} sans achat`}
                </p>
              </div>
              {/* A selection the filter hides would still take the next bulk change: start over. */}
              <Select value={filter} onValueChange={(v) => { setFilter(v as Filter); setSelected(new Set()) }}>
                <SelectTrigger className="w-60 bg-background" data-testid="sale-supply-filter" aria-label="Afficher">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Afficher : toutes les lignes</SelectItem>
                  <SelectItem value="todo">Afficher : à préciser</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {selected.size > 0 ? (
              <div className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm text-white" data-testid="sale-supply-bulk-bar">
                <span className="font-semibold">{selected.size} ligne{selected.size > 1 ? 's' : ''} sélectionnée{selected.size > 1 ? 's' : ''}</span>
                <span className="text-slate-400">Appro pour la sélection :</span>
                <Button size="sm" variant="secondary" disabled={busy} onClick={() => setBulkOpen(true)} data-testid="sale-supply-bulk-buy">Acheter chez…</Button>
                <Button size="sm" variant="secondary" disabled={busy} onClick={() => applyToSelection('make')} data-testid="sale-supply-bulk-make">Fabriquer</Button>
                <Button size="sm" variant="secondary" disabled={busy} onClick={() => applyToSelection('none')} data-testid="sale-supply-bulk-none">Aucun</Button>
                <span className="flex-1" />
                <Button size="sm" variant="ghost" className="text-slate-300 hover:bg-slate-800 hover:text-white"
                  onClick={() => setSelected(new Set())} data-testid="sale-supply-bulk-clear">Annuler</Button>
              </div>
            ) : null}

            <div className="overflow-x-auto rounded-lg border bg-background">
              <div className="min-w-[760px]">
                <div className="grid grid-cols-[36px_minmax(0,1fr)_70px_110px_320px] items-center gap-3 border-b bg-muted/40 px-4 py-2 text-xs font-medium text-muted-foreground">
                  <span />
                  <span>Ligne</span>
                  <span className="text-right">Qté</span>
                  <span className="text-right">Total HT</span>
                  <span>Appro</span>
                </div>
                {groups.length === 0 ? (
                  <p className="px-4 py-6 text-sm text-muted-foreground" data-testid="sale-supply-empty">Toutes les lignes ont leur appro.</p>
                ) : groups.map((group) => (
                  <div key={group.category}>
                    <div className="border-b px-4 pb-1.5 pt-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{group.category}</div>
                    {group.lines.map((line) => {
                      const hint = originQuantityHint(line)
                      const selectable = canWrite && !line.supply.locked
                      return (
                        <div
                          key={line.id}
                          className={cn('grid grid-cols-[36px_minmax(0,1fr)_70px_110px_320px] items-center gap-3 border-b px-4 py-2 text-sm last:border-b-0',
                            selected.has(line.id) && 'bg-slate-50')}
                          data-testid="sale-supply-line"
                          data-line-id={line.id}
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={selected.has(line.id)}
                            disabled={!selectable}
                            onChange={(e) => toggle(line.id, e.target.checked)}
                            aria-label={`Sélectionner ${line.title}`}
                            data-testid={`sale-supply-select-${line.id}`}
                          />
                          <span className="min-w-0">
                            <span className="block truncate">{line.title}</span>
                            {hint || line.supply.locked ? (
                              <span className="block truncate text-xs text-muted-foreground">
                                {[line.supply.locked ? 'commande fournisseur envoyée' : null, hint].filter(Boolean).join(' · ')}
                              </span>
                            ) : null}
                          </span>
                          <span className="text-right tabular-nums">{line.quantity ?? '—'}</span>
                          <span className="text-right tabular-nums">{line.lineTotalBeforeTax != null ? formatCurrency(line.lineTotalBeforeTax) : '—'}</span>
                          <span className="min-w-0">
                            <SupplyModeChip
                              supply={line.supply}
                              supplierName={line.supply.supplierId ? supplierNames.get(line.supply.supplierId) : null}
                              onClick={canWrite ? () => setEditing(line) : undefined}
                              testId={`sale-supply-chip-${line.id}`}
                            />
                          </span>
                        </div>
                      )
                    })}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      <LineSupplyDialog
        open={editing !== null}
        onOpenChange={(open) => { if (!open) setEditing(null) }}
        title={editing?.title ?? ''}
        subtitle={editing ? [
          `Vendu : ${editing.quantity ?? '—'}`,
          editing.lineTotalBeforeTax != null ? `${formatCurrency(editing.lineTotalBeforeTax)} HT` : null,
          editing.sourceSystem === 'winner' ? 'importée de Winner' : null,
          editing.originReference ? `réf. Winner ${editing.originReference}` : null,
        ].filter(Boolean).join(' · ') : null}
        initial={editing?.supply ?? null}
        suppliers={suppliers}
        soldQuantity={editing?.quantity ?? null}
        onSave={async (body) => {
          if (!editing) return
          await save([editing.id], body)
          showSuccessToast('Appro enregistré')
        }}
      />

      <LineSupplyDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        bulk
        title={`${selected.size} ligne${selected.size > 1 ? 's' : ''}`}
        suppliers={suppliers}
        onSave={async (body) => {
          const count = selected.size
          await save([...selected], { ...body, keepLineDetails: true })
          showSuccessToast(`Appro enregistré sur ${count} ligne${count > 1 ? 's' : ''}`)
        }}
      />
    </div>
  )
}
